/**
 * @file Login.tsx
 * @description
 * Component for the login/passcode entry page.
 * Allows users to enter their name and a passcode to gain access to the application.
 * Handles passcode submission and redirection based on the backend authentication response.
 * Automatically attempts to authenticate using existing cookie and localStorage name on mount.
 * Populates the name input from `localStorage` via `AuthContext`.
 *
 * @dependencies
 * - react: Standard React library.
 * - react-router-dom: For navigation.
 * - ../contexts/AuthContext: Provides authentication status, login function, and player name via hook.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext'; // Import useAuth hook

const Login: React.FC = () => {
  // Get isAuthenticated, login function, and playerName from AuthContext
  const { isAuthenticated, login, playerName: contextPlayerName } = useAuth();
  // Initialize local name state with value from AuthContext (which comes from localStorage)
  // Use a callback function for initialization to ensure it only runs once on mount.
  // Ensure initial value is trimmed and defaults to empty string if null.
  const [name, setName] = useState(() => contextPlayerName ? contextPlayerName.trim() : '');
  const [passcode, setPasscode] = useState('');

  const navigate = useNavigate();

  // Effect to check authentication status on component mount or when auth state changes
  useEffect(() => {
    // If isAuthenticated becomes true, redirect to lobby
    if (isAuthenticated === true) {
      console.log('Login page: User is authenticated, redirecting to lobby...');
      // Using navigate with replace: true prevents going back to login via browser back button
      navigate('/lobby', { replace: true });
    }
    // If isAuthenticated becomes false, the form is shown.
    // If isAuthenticated is null, the loading state from AuthRedirect handles it.
  }, [isAuthenticated, navigate]); // Depend on isAuthenticated and navigate

  // Effect to update local name state ONLY when contextPlayerName changes.
  // This handles cases where the name is loaded from localStorage by checkAuth *after*
  // the initial render, or when contextPlayerName becomes null (e.g., on logout).
  // Importantly, this effect does *not* depend on the local `name` state, preventing feedback loops.
  useEffect(() => {
      console.log('Login page Effect: contextPlayerName changed:', contextPlayerName);
      // If contextPlayerName has a value, update the local state unless it's already the same.
      // This handles the case where checkAuth finds a name after the component mounts.
      if (contextPlayerName !== null && name !== contextPlayerName.trim()) {
           console.log('Login page Effect: Updating local name from context.');
           setName(contextPlayerName.trim());
      } else if (contextPlayerName === null && name !== '') {
          // If contextPlayerName becomes null (e.g., on logout), clear the local name input.
          console.log('Login page Effect: contextPlayerName became null, clearing local name.');
           setName('');
      }
      // Removing `name` from dependencies resolves the typing issue.
      // We also don't need to compare `contextPlayerName.trim()` with `name` *within* the conditional checks
      // as the effect only runs when `contextPlayerName` changes, and the `useState` initializer
      // handles the initial value based on `contextPlayerName`.
      // A simplified approach: just set the name from context if it's not null, and clear if null.
      // This simpler logic still requires the local name != contextName check to avoid infinite loop IF
      // setting local state causes the effect to re-run (which it doesn't if `name` is not a dependency).
      // Let's stick to the simpler condition: if context has a name, set local name. If context has no name, clear local name.
      // No, the check `name !== contextPlayerName.trim()` *is* needed to prevent setting the state redundantly
      // when the component re-renders for other reasons but contextPlayerName hasn't changed.
      // Let's keep the logic but remove the dependency on `name`.
      // Okay, simplest working logic: If contextPlayerName is not null, set name from context. If it is null, clear name.
      // This still feels wrong because setting state inside useEffect without checking if the new value is different
      // can cause render loops if the state update itself somehow triggers the effect (e.g. if the state value
      // being set is also implicitly a dependency of the effect, which `name` *would* be if it were in the deps array).

      // Let's use the robust check but remove 'name' from dependencies.
      // The dependency is now only `contextPlayerName`. The effect runs when `contextPlayerName` changes.
      // Inside the effect, we compare the *new* `contextPlayerName` with the *current* `name` state.
      // If they differ, we update the local `name` state. This will trigger a re-render,
      // but the effect won't run again because `contextPlayerName` hasn't changed *again*.
      // This correctly syncs the local state to the context state *when the context state changes*.

       // Final attempt at the correct effect logic:
       if (contextPlayerName !== null) {
           const trimmedContextName = contextPlayerName.trim();
           // Only update if the value from context is different from the current local state
           if (name !== trimmedContextName) {
                console.log(`Login page Effect: contextPlayerName (${contextPlayerName}) is not null and differs from local name (${name}). Updating local name.`);
                setName(trimmedContextName);
           } else {
               console.log(`Login page Effect: contextPlayerName (${contextPlayerName}) is not null but matches local name (${name}). No update needed.`);
           }
       } else { // contextPlayerName is null
            // Only clear local state if it's not already empty
            if (name !== '') {
                 console.log(`Login page Effect: contextPlayerName is null and local name (${name}) is not empty. Clearing local name.`);
                 setName('');
            } else {
                 console.log(`Login page Effect: contextPlayerName is null and local name is already empty. No action.`);
            }
       }


  }, [contextPlayerName]); // ONLY depend on contextPlayerName

  /**
   * Handles the form submission for login.
   * Prevents default form submission, calls the login function from AuthContext,
   * and navigates based on the result.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Basic validation - name is now handled internally by login function, but check here too for UI feedback
    const trimmedName = name.trim();
    if (!trimmedName) {
       alert('Please enter your name.'); // Simple alert for now
       return;
    }
    if (!passcode) {
      alert('Please enter the passcode.'); // Simple alert for now
      return;
    }

    console.log(`Login page: Attempting login for user "${trimmedName}"`);

    // Call the login function from AuthContext, passing the trimmed name
    // AuthContext's login function now handles saving the name to localStorage on success.
    const success = await login(passcode, trimmedName);

    // The useEffect above will handle navigation to /lobby if success is true.
    // If login was not successful, navigate to the wrong passcode page.
    if (!success) {
      console.log('Login page: Login failed, navigating to wrong-passcode...');
      // AuthContext now clears playerName on failed login internally
      navigate('/wrong-passcode', { replace: true });
    }
  };

  // If authentication is null (checking), show nothing or a loading spinner.
  // AuthRedirect in App.tsx also handles a loading state.
  // We only render the login form when isAuthenticated is explicitly false.
  if (isAuthenticated === null) {
     // The AuthRedirect component handles showing the initial "Loading authentication status..." message.
     // Returning null here allows AuthRedirect to control the full page content in this state.
     return null;
  }


  // If isAuthenticated is false, render the login form
  // playerName input is pre-filled from contextPlayerName (localStorage) via the state initialization and useEffect
  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100">
      <h1 className="text-4xl font-bold mb-8">The Game</h1>
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-700 p-8 rounded-lg shadow-md w-80">
        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Name</label>
          <input
            type="text"
            id="name"
            value={name} // Controlled component, value comes from local state
            onChange={(e) => setName(e.target.value)} // Update local state on change
            className="w-full px-3 py-2 border rounded-md dark:bg-gray-600 dark:border-gray-500 dark:text-gray-200"
            required
            autoFocus // Focus on the name input
          />
        </div>
        <div className="mb-6">
          <label htmlFor="passcode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Passcode</label>
          <input
            type="password"
            id="passcode"
            value={passcode} // Controlled component, value comes from local state
            onChange={(e) => setPasscode(e.target.value)} // Update local state on change
            className="w-full px-3 py-2 border rounded-md dark:bg-gray-600 dark:border-gray-500 dark:text-gray-200"
            required
          />
        </div>
        <button
          type="submit"
          className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition-colors"
        >
          Enter
        </button>
      </form>
    </div>
  );
};

export default Login;